import { useState } from 'react';
import BookingSummary from './BookingSummary';
import { specialLabelsFor, isMealPastCutoff, isDateSeasonClosed } from './specialDates';
import { API_BASE, todayCST } from './config';
import { useTheme, tokens } from './theme';

// `bypassCutoff` is used by the Admin Act-As flow to allow late bookings
// past the normal member cutoff times. It does NOT affect regular member logins.
export default function HotelCheckin({ memberNumber, onSubmitted, bypassCutoff = false }) {
  const [theme] = useTheme();
  const t = tokens(theme);
  const [checkin, setCheckin] = useState('');
  const [checkout, setCheckout] = useState('');
  const [people, setPeople] = useState([]);
  const [name, setName] = useState('');
  const [isGuest, setIsGuest] = useState(false);
  const [isChild, setIsChild] = useState(false);
  const [showBookingSummary, setShowBookingSummary] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const makeId = () => {
    try {
      if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
      }
    } catch {}
    return `id_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  };

  const ymdToLocalDate = (ymd) => {
    const [y, m, d] = (ymd || '').split('-').map(Number);
    return new Date(y, m - 1, d);
  };

  const getDateRange = (startYmd, endYmd) => {
    const dates = [];
    if (!startYmd || !endYmd) return dates;
    let cur = ymdToLocalDate(startYmd);
    const end = ymdToLocalDate(endYmd);
    while (cur <= end) {
      const y = cur.getFullYear();
      const m = String(cur.getMonth() + 1).padStart(2, '0');
      const d = String(cur.getDate()).padStart(2, '0');
      dates.push(`${y}-${m}-${d}`);
      cur.setDate(cur.getDate() + 1);
    }
    return dates;
  };

  const addPerson = () => {
    if (!name || !checkin || !checkout) {
      alert('Please fill in all required fields.');
      return;
    }

    const meals = getDateRange(checkin, checkout).map((date) => ({
      date,
      breakfast: false,
      lunch: false,
      dinner: false,
    }));

    setPeople((prev) => [...prev, { name, isGuest, isChild, meals }]);

    setName('');
    setIsGuest(false);
    setIsChild(false);
  };

  const removePerson = (index) => {
    setPeople((prev) => prev.filter((_, i) => i !== index));
  };

  const toggleMeal = (personIdx, dayIdx, meal) => {
    setPeople((prev) => {
      const updated = [...prev];
      updated[personIdx] = { ...updated[personIdx] };
      updated[personIdx].meals = [...updated[personIdx].meals];
      const day = { ...updated[personIdx].meals[dayIdx] };

      if (isMealPastCutoff(meal, day.date, undefined, bypassCutoff)) {
        alert(`${meal} cutoff passed for ${day.date}.`);
        return prev;
      }

      day[meal] = !day[meal];
      updated[personIdx].meals[dayIdx] = day;
      return updated;
    });
  };

  const submitToSheet = async () => {
    if (submitting) return;
    const rows = [];

    people.forEach((person) => {
      const nameWithTags = `${person.name}${person.isChild ? ' (c)' : ''}${
        person.isGuest ? ' (G)' : ''
      }`;

      person.meals.forEach((meal) => {
        const breakfast = meal.breakfast ? 'Yes' : '';
        const lunch = meal.lunch ? 'Yes' : '';
        const dinner = meal.dinner ? 'Yes' : '';

        if (!breakfast && !lunch && !dinner) return;

        rows.push({
          id: makeId(),
          date: meal.date,
          name: nameWithTags,
          member: person.isGuest ? '' : 'Yes',
          breakfast,
          lunch,
          dinner,
          memberNumber,
        });
      });
    });

    if (rows.length === 0) {
      alert('No meals selected.');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(API_BASE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: rows }),
      });

      if (response.ok) {
        alert('Submitted Successfully');
        setPeople([]);
        if (typeof onSubmitted === 'function') {
          await onSubmitted();
        }
      } else {
        const errText = await response.text().catch(() => '');
        alert('Something went wrong.' + (errText ? `\n\n${errText}` : ''));
      }
    } catch (err) {
      console.error('submitToSheet failed', err);
      alert('Submission failed. Check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const input = { background: t.inputBg, color: t.inputText, border: `1px solid ${t.inputBorder}`, borderRadius: 6, padding: '6px 8px' };
  const btn = { background: t.inputBg, color: t.text, border: `1px solid ${t.inputBorder}`, borderRadius: 6, padding: '6px 12px', cursor: 'pointer' };

  return (
    <div style={{ padding: '20px', fontFamily: 'Arial', color: t.text }}>
      <h2 style={{ color: t.textStrong }}>Check-In</h2>

      {bypassCutoff && (
        <div
          style={{
            marginBottom: 10,
            padding: '8px 12px',
            borderRadius: 8,
            background: 'rgba(234,179,8,0.15)',
            border: '1px solid rgba(234,179,8,0.55)',
            color: t.text,
            fontSize: 13,
          }}
        >
          <b>Cutoff override is ON.</b> Date restrictions and meal-time cutoffs are bypassed for this admin session.
        </div>
      )}

      <div>
        <label>Check-in Date: </label>
        <input
          type="date"
          value={checkin}
          onChange={(e) => setCheckin(e.target.value)}
          min={bypassCutoff ? undefined : todayCST()}
          style={input}
        />
      </div>

      <div>
        <label>Check-out Date: </label>
        <input
          type="date"
          value={checkout}
          onChange={(e) => setCheckout(e.target.value)}
          min={bypassCutoff ? undefined : (checkin || todayCST())}
          style={input}
        />
      </div>

      <div style={{ marginTop: '10px' }}>
        <div>
          <label>Full Name: </label>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} style={input} />
        </div>

        <div style={{ marginTop: 8 }}>
          <label>
            <input
              type="checkbox"
              checked={isGuest}
              onChange={(e) => setIsGuest(e.target.checked)}
            />{' '}
            Guest
          </label>
          <label style={{ marginLeft: 12 }}>
            <input
              type="checkbox"
              checked={isChild}
              onChange={(e) => setIsChild(e.target.checked)}
            />{' '}
            Child
          </label>
        </div>

        <button style={{ ...btn, marginTop: 10 }} onClick={addPerson}>
          Add Person
        </button>
      </div>

      {people.map((p, idx) => (
        <div
          key={idx}
          style={{
            border: `1px solid ${t.border}`,
            background: t.cardAlt,
            padding: 10,
            marginTop: 12,
            borderRadius: 8,
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
            <b>
              {p.name} {p.isChild ? '(Child)' : ''} {p.isGuest ? '(Guest)' : ''}
            </b>
            <button style={btn} onClick={() => removePerson(idx)}>Remove</button>
          </div>

          <div style={{ marginTop: 10 }}>
            {p.meals.map((day, dayIdx) => {
              const spec = specialLabelsFor(day.date);
              const label = (m) => spec.label[m];
              const enabled = (m) => spec.enabled[m];

              const mealButton = (meal) => {
                if (!enabled(meal) || !label(meal)) {
                  return (
                    <button key={meal} disabled style={{ ...btn, opacity: 0.4 }}>
                      —
                    </button>
                  );
                }
                const locked = isMealPastCutoff(meal, day.date, undefined, bypassCutoff);
                const on = day[meal];
                return (
                  <button
                    key={meal}
                    disabled={locked}
                    onClick={() => toggleMeal(idx, dayIdx, meal)}
                    style={{
                      padding: '6px 10px',
                      borderRadius: 6,
                      border: `1px solid ${on ? t.ok : t.inputBorder}`,
                      background: on ? t.pillOnBg : t.inputBg,
                      color: on ? t.pillOnFg : t.text,
                      opacity: locked ? 0.5 : 1,
                      cursor: locked ? 'not-allowed' : 'pointer',
                    }}
                    title={locked ? 'Cutoff passed' : 'Toggle'}
                  >
                    {label(meal)}
                  </button>
                );
              };

              const dayLabel = (() => {
                const dt = ymdToLocalDate(day.date);
                const dayName = dt.toLocaleDateString('en-US', { weekday: 'long' });
                return `${dayName}, ${day.date}`;
              })();

              // Explain a fully-blacked-out day, otherwise the member just sees
              // three greyed-out buttons with no reason given.
              const seasonClosed = !bypassCutoff && isDateSeasonClosed(day.date);

              return (
                <div key={day.date} style={{ marginBottom: 10 }}>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>
                    {dayLabel}
                    {seasonClosed && (
                      <span style={{ marginLeft: 8, fontWeight: 400, fontSize: 12, color: t.muted }}>
                        — no meals served (season closed)
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {mealButton('breakfast')}
                    {mealButton('lunch')}
                    {mealButton('dinner')}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      <button
        style={{
          marginTop: 14,
          padding: '10px 14px',
          fontSize: 16,
          border: 'none',
          borderRadius: 8,
          background: t.ok,
          color: 'white',
          fontWeight: 700,
          opacity: submitting ? 0.6 : 1,
          cursor: submitting ? 'not-allowed' : 'pointer',
        }}
        onClick={submitToSheet}
        disabled={submitting}
      >
        {submitting ? 'Submitting…' : 'Submit'}
      </button>

      <div style={{ marginTop: 18 }}>
        <button style={btn} onClick={() => setShowBookingSummary((s) => !s)}>
          {showBookingSummary ? 'Hide Booking Summary' : 'Show Booking Summary'}
        </button>
        {showBookingSummary && (
          <div style={{ marginTop: 10 }}>
            <BookingSummary />
          </div>
        )}
      </div>
    </div>
  );
}
